import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { Playlist, PublicPlaylist } from "@gooner/shared";
import { IconPlay } from "../ui/icons";
import { GoldName } from "../ui/Gold";
import { cx } from "../ui/primitives";
import { MASK } from "../../lib/replayMask";

/**
 * The normalized shape a collection CARD renders from — the common subset of
 * `PublicPlaylist` (discovery) and `Playlist` (a user's own). Map either into this
 * with {@link fromPublicPlaylist} / {@link fromPlaylist} so one card serves both the
 * Editor's Choice / community grids and the signed-in "Your collections" grid.
 */
export interface CollectionCardData {
  id: string;
  name: string;
  clipCount: number;
  description?: string;
  thumbnailPosterUrl?: string;
  ownerName?: string | null;
  ownerGold?: boolean;
  editorsChoice?: boolean;
}

export function fromPublicPlaylist(c: PublicPlaylist): CollectionCardData {
  return {
    id: c.id,
    name: c.name,
    clipCount: c.clipCount,
    description: c.description,
    thumbnailPosterUrl: c.thumbnailPosterUrl,
    ownerName: c.ownerName,
    ownerGold: c.ownerGold,
    editorsChoice: c.editorsChoice,
  };
}

export function fromPlaylist(p: Playlist): CollectionCardData {
  return {
    id: p.id,
    name: p.name,
    clipCount: p.clipCount,
    description: p.description,
    thumbnailPosterUrl: p.thumbnailPosterUrl,
    editorsChoice: p.editorsChoice,
  };
}

/**
 * One collection card: cover, name, meta, and an optional hover Play affordance.
 * The whole card links to `to`; `onPlay` (when given) opens the ad-hoc grid display
 * without navigating. Shared by Browse-style discovery grids and the Collections hub.
 */
export function CollectionCard({
  collection: c,
  to,
  onPlay,
  eager = false,
}: {
  collection: CollectionCardData;
  to: string;
  onPlay?: () => void;
  /** Above the fold: load the cover now instead of lazily. */
  eager?: boolean;
}) {
  return (
    /* The focus ring rides THIS box when its link is focused (QA 2026-10-21: a tabbed card
       showed none). On the link itself the browser outline was clipped by overflow-hidden
       and, drawn inset, painted UNDER the positioned thumbnail. A ring here sits outside
       the clip; the classes are primitives.tsx's focus ring. */
    <div className="group relative overflow-hidden rounded-xl border border-[var(--color-edge)] transition-colors hover:border-[var(--color-accent)]/50 has-[>a:focus-visible]:ring-2 has-[>a:focus-visible]:ring-[var(--color-accent-soft)]/60">
      <Link to={to} className="block text-left focus-visible:outline-none">
        <div className="relative aspect-video bg-black">
          {c.thumbnailPosterUrl ? (
            <img
              src={c.thumbnailPosterUrl}
              alt=""
              className="h-full w-full object-cover opacity-90"
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              // The box is aspect-video and the img fills it; this is the box, not the file.
              width={320}
              height={180}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-zinc-600">◆</div>
          )}
          {c.editorsChoice ? (
            <span className="absolute left-2 top-2 rounded bg-[var(--color-accent)]/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white">
              Editor's Choice
            </span>
          ) : null}
          <span className="absolute bottom-2 right-2 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-zinc-200">
            {c.clipCount} clip{c.clipCount === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-3">
          <div className="truncate text-sm font-medium text-zinc-100" {...MASK}>{c.name}</div>
          {/* Two stacked lines, not an either/or: the byline used to be the description's
              else-branch, which hid every curator who had written one — and a curator's
              GOLD chip must not depend on their description habits. */}
          {c.description ? (
            <p className="mt-0.5 line-clamp-2 text-xs text-zinc-500" {...MASK}>{c.description}</p>
          ) : null}
          {c.ownerName ? (
            <p className="mt-0.5 flex items-center text-xs text-zinc-500">
              <GoldName name={<>by {c.ownerName}</>} gold={c.ownerGold} />
            </p>
          ) : null}
        </div>
      </Link>

      {onPlay ? (
        <button
          type="button"
          title="Play this collection"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPlay();
          }}
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-1 text-[11px] font-medium text-white opacity-0 transition-opacity hover:bg-[var(--color-accent)] focus-visible:opacity-100 group-hover:opacity-100"
        >
          <IconPlay width={12} height={12} /> Play
        </button>
      ) : null}
    </div>
  );
}

/**
 * A collection card's box with nothing in it, for a shelf that has not answered yet.
 * It draws what CollectionCard draws: the same border, the same 16:9 cover and the same
 * p-3 body, a text-sm name line and a TWO-line text-xs description, each held by a
 * non-breaking space. Two description lines because the first card on "Your collections"
 * is always the virtual Faps collection, whose description is two lines, and the
 * tallest card is what sets the row's height.
 */
export function CollectionCardSkeleton({ className }: { className?: string }) {
  return (
    <div aria-hidden data-collection-skeleton className={cx("overflow-hidden rounded-xl border border-[var(--color-edge)]", className)}>
      <div className="aspect-video animate-pulse bg-white/5" />
      <div className="p-3">
        <div className="truncate text-sm font-medium">{"\u00a0"}</div>
        <p className="mt-0.5 text-xs">
          {"\u00a0"}
          <br />
          {"\u00a0"}
        </p>
      </div>
    </div>
  );
}

/** One row of the hub's four-up grid in placeholders: as many as the row holds at each
 *  width (one on a phone, two from sm:, four from lg:), so it is one row tall everywhere. */
export function CollectionSkeletonRow() {
  return (
    <CollectionGrid wide>
      <CollectionCardSkeleton />
      <CollectionCardSkeleton className="hidden sm:block" />
      <CollectionCardSkeleton className="hidden lg:block" />
      <CollectionCardSkeleton className="hidden lg:block" />
    </CollectionGrid>
  );
}

/** A titled section wrapper — shared across the collection grids. */
export function CollectionSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-zinc-400">{title}</h2>
      {children}
    </section>
  );
}

/** The responsive card grid used by every collection listing.
 *
 *  `wide` runs FOUR columns from `lg:` up instead of three — the collections hub's
 *  choice, where 8-item pages land as exactly two rows. The default stays 3-up on
 *  purpose: this grid also lays out the views library and the quick setups, whose
 *  cards were sized against three columns, and a shared default silently re-laying
 *  those pages is how a "collections tweak" becomes a views regression. */
export function CollectionGrid({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className={cx("grid grid-cols-1 gap-4 sm:grid-cols-2", wide ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
      {children}
    </div>
  );
}
